/**
 * Lightweight hash-based router (no react-router dep).
 * Hash format examples:
 * - "#/chat" → chat
 * - "#/admin" → admin dashboard
 * - "#/admin/forms" → forms list
 * - "#/admin/forms/pr_standard" → form builder cho code "pr_standard"
 * - "#/settings/personal" → personal settings
 */
import { create } from "zustand";

export type RouteName =
  | "chat"
  | "admin"
  | "admin.forms"
  | "admin.forms.edit"
  | "admin.workflows"
  | "admin.workflows.edit"
  | "admin.notifications"
  | "admin.translations"
  | "admin.audit"
  | "admin.odooModules"
  | "admin.externalSystems"
  | "admin.health"
  | "admin.cost"
  | "admin.localLlm"
  | "admin.insights"
  | "admin.users"
  | "me.insights"
  | "admin.backup"
  | "admin.dashboards"
  | "admin.dashboards.detail"
  | "settings"
  | "settings.personal"
  | "settings.tenant"
  | "settings.secrets"
  | "settings.password"
  | "settings.overrides"
  | "settings.presets"
  | "settings.notifications"
  | "settings.sessions"
  | "settings.account"
  | "settings.apiTokens";

interface ParsedRoute {
  name: RouteName;
  param?: string;
}

function parseHash(hash: string): ParsedRoute {
  const h = hash.replace(/^#/, "").replace(/^\//, "");
  if (h === "" || h === "chat") return { name: "chat" };
  if (h.startsWith("admin/forms/")) {
    const param = h.slice("admin/forms/".length);
    return { name: "admin.forms.edit", param };
  }
  if (h === "admin/forms") return { name: "admin.forms" };
  if (h.startsWith("admin/workflows/")) {
    const param = h.slice("admin/workflows/".length);
    return { name: "admin.workflows.edit", param };
  }
  if (h === "admin/workflows") return { name: "admin.workflows" };
  if (h === "admin/notifications") return { name: "admin.notifications" };
  if (h === "admin/translations") return { name: "admin.translations" };
  if (h === "admin/audit") return { name: "admin.audit" };
  if (h === "admin/odoo-modules") return { name: "admin.odooModules" };
  if (h === "admin/external-systems") return { name: "admin.externalSystems" };
  if (h === "admin/health") return { name: "admin.health" };
  if (h === "admin/cost") return { name: "admin.cost" };
  if (h === "admin/local-llm") return { name: "admin.localLlm" };
  if (h === "admin/insights") return { name: "admin.insights" };
  if (h === "admin/users") return { name: "admin.users" };
  if (h === "me/insights") return { name: "me.insights" };
  if (h === "admin/backup") return { name: "admin.backup" };
  if (h.startsWith("admin/dashboards/")) {
    const param = h.slice("admin/dashboards/".length);
    return { name: "admin.dashboards.detail", param };
  }
  if (h === "admin/dashboards") return { name: "admin.dashboards" };
  if (h === "admin") return { name: "admin" };
  if (h.startsWith("settings/personal")) return { name: "settings.personal" };
  if (h.startsWith("settings/tenant")) return { name: "settings.tenant" };
  if (h.startsWith("settings/secrets")) return { name: "settings.secrets" };
  if (h.startsWith("settings/password")) return { name: "settings.password" };
  if (h.startsWith("settings/overrides")) return { name: "settings.overrides" };
  if (h.startsWith("settings/presets")) return { name: "settings.presets" };
  if (h.startsWith("settings/notifications")) return { name: "settings.notifications" };
  if (h.startsWith("settings/sessions")) return { name: "settings.sessions" };
  if (h.startsWith("settings/account")) return { name: "settings.account" };
  if (h.startsWith("settings/api-tokens")) return { name: "settings.apiTokens" };
  if (h.startsWith("settings")) return { name: "settings" };
  return { name: "chat" };
}

interface RouteState {
  current: RouteName;
  param: string | undefined;
  navigate: (to: RouteName, param?: string) => void;
}

function nameToPath(to: RouteName, param?: string): string {
  if (to === "admin.forms.edit" && param) return `admin/forms/${param}`;
  if (to === "admin.workflows.edit" && param) return `admin/workflows/${param}`;
  if (to === "admin.odooModules") return "admin/odoo-modules";
  if (to === "admin.externalSystems") return "admin/external-systems";
  if (to === "admin.health") return "admin/health";
  if (to === "admin.cost") return "admin/cost";
  if (to === "admin.localLlm") return "admin/local-llm";
  if (to === "admin.insights") return "admin/insights";
  if (to === "admin.users") return "admin/users";
  if (to === "me.insights") return "me/insights";
  if (to === "admin.backup") return "admin/backup";
  if (to === "admin.dashboards.detail" && param) return `admin/dashboards/${param}`;
  if (to === "admin.dashboards") return "admin/dashboards";
  return to.replace(/\./g, "/");
}

const initial =
  typeof window !== "undefined"
    ? parseHash(window.location.hash)
    : { name: "chat" as const };

export const useRouteStore = create<RouteState>((set) => ({
  current: initial.name,
  param: initial.param,
  navigate: (to, param) => {
    const path = nameToPath(to, param);
    if (typeof window !== "undefined") {
      window.location.hash = `/${path}`;
    }
    set({ current: to, param });
  },
}));

if (typeof window !== "undefined") {
  window.addEventListener("hashchange", () => {
    const parsed = parseHash(window.location.hash);
    useRouteStore.setState({ current: parsed.name, param: parsed.param });
  });
}
